export type BusinessIdType = "EIN" | "CBN";

const EIN_PATTERN = /^\d{2}-\d{7}$/;
const CBN_PATTERN = /^\d{9}$/;
const MAX_BUSINESS_ID_DIGITS = 9;

function isDigit(char: string): boolean {
  return char >= "0" && char <= "9";
}

function countDigits(value: string): number {
  let count = 0;
  for (const char of value) {
    if (isDigit(char)) {
      count += 1;
    }
  }
  return count;
}

/**
 * Returns a settings i18n key when the identifier is invalid, otherwise null.
 */
export function getBusinessIdHelperKey(
  type: BusinessIdType,
  value: string
): "einHelpText" | "cbnHelpText" | null {
  if (type === "EIN") {
    return EIN_PATTERN.test(value) ? null : "einHelpText";
  }

  return CBN_PATTERN.test(value) ? null : "cbnHelpText";
}

export function isValidBusinessId(type: BusinessIdType, value: string): boolean {
  return getBusinessIdHelperKey(type, value) === null;
}

export function formatBusinessId(type: BusinessIdType, value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, MAX_BUSINESS_ID_DIGITS);
  if (type === "CBN" || digits.length <= 2) {
    return digits;
  }

  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

export function getBusinessIdCaretIndex(
  rawValue: string,
  caret: number,
  formatted: string
): number {
  const safeCaret = Math.max(0, Math.min(caret, rawValue.length));
  const prefix = rawValue.slice(0, safeCaret);
  const digitsBeforeCaret = countDigits(prefix);
  if (digitsBeforeCaret === 0) {
    return 0;
  }

  let seen = 0;
  let index = 0;
  for (; index < formatted.length; index++) {
    if (isDigit(formatted[index])) {
      seen += 1;
      if (seen === digitsBeforeCaret) {
        index += 1;
        break;
      }
    }
  }

  if (prefix.length > 0 && !isDigit(prefix[prefix.length - 1])) {
    while (index < formatted.length && !isDigit(formatted[index])) {
      index += 1;
    }
  }

  return index;
}
